import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Manages cart drawer behavior inside a <theme-drawer>.
 *
 * Responsibilities:
 * - Refresh the cart drawer after cart changes
 * - Open the cart drawer after an item is added
 * - Update the sticky cart summary
 * - Close the drawer when the installments CTA is clicked
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #isRefreshing = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Runs whenever the cart drawer opens.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const paymentTerms = this.querySelector('shopify-payment-terms');

      const installmentsCta =
        paymentTerms?.shadowRoot?.querySelector(
          '#shopify-installments-cta'
        );

      installmentsCta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Refreshes the cart items component so the newest cart contents
   * appear without refreshing the browser page.
   */
  async #refreshCartDrawer() {
    if (this.#isRefreshing) return;

    this.#isRefreshing = true;

    try {
      await customElements.whenDefined('cart-items-component');

      const cartItemsComponents =
        document.querySelectorAll('cart-items-component');

      if (cartItemsComponents.length === 0) {
        console.warn(
          '[cart-drawer] No cart-items-component was found.'
        );

        return;
      }

      const refreshPromises = [];

      for (const cartItemsComponent of cartItemsComponents) {
        if (
          typeof cartItemsComponent.fetchCartData === 'function'
        ) {
          refreshPromises.push(
            cartItemsComponent.fetchCartData()
          );
        }
      }

      if (refreshPromises.length > 0) {
        await Promise.allSettled(refreshPromises);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn(
          '[cart-drawer] Failed to refresh cart contents:',
          error
        );
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Handles cart add, update and removal events.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add';

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    const finishUpdate = async (detail) => {
      if (detail?.didError) {
        this.#updateStickyState();
        return;
      }

      /*
       * Force the drawer contents to load the current Shopify cart.
       * This fixes products appearing only after a page refresh.
       */
      await this.#refreshCartDrawer();

      const openAndSettle = () => {
        if (
          shouldAutoOpen &&
          !this.#themeDrawer?.isOpen
        ) {
          this.#themeDrawer?.open();
        }

        requestAnimationFrame(() => {
          this.#updateStickyState();
        });
      };

      if (shouldAutoOpen && sourceModal?.open) {
        sourceModal.addEventListener(
          'close',
          openAndSettle,
          { once: true }
        );

        return;
      }

      openAndSettle();
    };

    if (event.promise) {
      event.promise
        .then(({ detail }) => finishUpdate(detail))
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn(
              '[cart-drawer] Cart update promise rejected:',
              error
            );
          }

          this.#refreshCartDrawer().then(() => {
            requestAnimationFrame(() => {
              this.#updateStickyState();
            });
          });
        });

      return;
    }

    /*
     * Fallback for cart actions that do not provide an event promise.
     */
    this.#refreshCartDrawer().then(() => {
      if (
        shouldAutoOpen &&
        !this.#themeDrawer?.isOpen
      ) {
        this.#themeDrawer?.open();
      }

      requestAnimationFrame(() => {
        this.#updateStickyState();
      });
    });
  };

  /**
   * Updates whether the cart summary should remain sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    if (drawerHeight <= 0) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}